import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  getGetApiCompaniesCurrentAccountmanagementEmployeesQueryKey,
  getGetApiCompaniesCurrentEmployeesQueryKey,
  getGetApiCompaniesCurrentGroupsQueryKey,
  useGetApiCompaniesCurrentServiceconnections,
  usePostApiCompaniesCurrentServiceconnectionsServiceConnectionIdSyncEmployees,
} from '@/api/generated/company-management-user/company-management-user'
import type { ServiceConnectionModel } from '@/api/model'
import { TableCard } from '@/components/common/table/TableCard'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { notify } from '@/lib/notify'
import { ServiceConnectionsDataTable } from './ServiceConnectionsDataTable'

interface Props {
  canEdit: boolean
  onEdit: (connection: ServiceConnectionModel) => void
}

export function ServiceConnectionsTable({ canEdit, onEdit }: Props) {
  const { t } = useTranslation('service-connections')
  const queryClient = useQueryClient()
  const [syncConnection, setSyncConnection] = useState<ServiceConnectionModel | null>(null)

  // The endpoint returns every connection of the company at once — no paging, no sorting.
  const { data, isLoading, isError } = useGetApiCompaniesCurrentServiceconnections()
  const connections = data?.data ?? []

  const { mutate: syncEmployees, isPending: isSyncing } =
    usePostApiCompaniesCurrentServiceconnectionsServiceConnectionIdSyncEmployees({
      mutation: {
        // The sync runs synchronously in the request and can take a while — acknowledge
        // the click right away. The result notification follows (BON-2014 pattern).
        onMutate: () =>
          notify.info(t('toasts.syncStarted'), { description: t('toasts.syncStartedHint') }),
        onSuccess: () => {
          notify.success(t('toasts.syncSucceeded'))
          setSyncConnection(null)
          // The sync creates, updates and deletes employees and may add missing groups.
          queryClient.invalidateQueries({
            queryKey: getGetApiCompaniesCurrentAccountmanagementEmployeesQueryKey(),
          })
          queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentEmployeesQueryKey() })
          queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentGroupsQueryKey() })
        },
        onError: (error) => notify.apiError(error, t('toasts.syncError')),
      },
    })

  return (
    <>
      <TableCard>
        <div className="flex-1 overflow-auto scrollbar-custom p-0 relative">
          <ServiceConnectionsDataTable
            isLoading={isLoading}
            isError={isError}
            connections={connections}
            canEdit={canEdit}
            onEdit={onEdit}
            onSyncEmployees={setSyncConnection}
          />
        </div>
      </TableCard>

      <ConfirmDialog
        open={syncConnection !== null}
        title={t('list.sync.title')}
        description={t('list.sync.description', { name: syncConnection?.name ?? '' })}
        confirmLabel={t('list.sync.confirm')}
        isLoading={isSyncing}
        onConfirm={() =>
          syncConnection && syncEmployees({ serviceConnectionId: syncConnection.id })
        }
        onClose={() => setSyncConnection(null)}
      />
    </>
  )
}
